/**
 * Session 7 Phase 1 — Platform-Operator layout.
 *
 * Distinct from the tenant AppLayout: no tenant-slug chip in the
 * topbar, no wizard, no concierge empty-state. Pure admin surface.
 *
 * Role-gated: any non-operator hitting /operator/* is bounced to
 * /app/home with a toast. Backend enforces the same via
 * require_platform_owner() — this guard is UX-only.
 */
import { useEffect } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { auth } from "../../lib/api";
import { toast } from "sonner";
import {
  LayoutDashboard, Building2, ClipboardCheck, LineChart, Settings,
  LogOut, ShieldCheck, Activity, Flag, Users2, CreditCard,
} from "lucide-react";

const NAV = [
  { to: "/operator/dashboard",       label: "Dashboard",       icon: LayoutDashboard,
    testId: "operator-nav-dashboard" },
  { to: "/operator/tenants",         label: "Tenants",         icon: Building2,
    testId: "operator-nav-tenants" },
  { to: "/operator/approvals",       label: "Freigaben",       icon: ClipboardCheck,
    testId: "operator-nav-approvals" },
  { to: "/operator/plans",           label: "Pläne",           icon: CreditCard,
    testId: "operator-nav-plans" },
  { to: "/operator/feature-flags",   label: "Feature-Flags",   icon: Flag,
    testId: "operator-nav-flags" },
  { to: "/operator/operators",       label: "Operatoren",      icon: Users2,
    testId: "operator-nav-operators" },
  { to: "/operator/audit",           label: "Audit",           icon: ShieldCheck,
    testId: "operator-nav-audit" },
  { to: "/operator/health",          label: "Health",          icon: Activity,
    testId: "operator-nav-health" },
  { to: "/operator/weekly-reports",  label: "Weekly Reports",  icon: LineChart,
    testId: "operator-nav-weekly" },
  { to: "/operator/settings",        label: "Einstellungen",   icon: Settings,
    testId: "operator-nav-settings" },
];

export default function OperatorLayout() {
  const nav = useNavigate();
  const user = auth.getUser() || {};

  useEffect(() => {
    if (!user || user.role !== "platform_owner") {
      toast.error("Kein Operator-Zugriff.");
      nav("/app/home", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!user || user.role !== "platform_owner") return null;

  const handleLogout = () => {
    auth.clear();
    nav("/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex"
         data-testid="operator-layout">
      <aside className="w-60 shrink-0 border-r border-border bg-card/50 flex flex-col">
        <div className="px-5 py-5 border-b border-border">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">
            VIONA
          </div>
          <div className="text-lg font-semibold">Operator-Konsole</div>
        </div>
        <nav className="flex-1 py-3 space-y-1 px-2">
          {NAV.map(({ to, label, icon: Icon, testId }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={testId}
              className={({ isActive }) =>
                `flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors ${
                  isActive
                    ? "bg-primary text-primary-foreground"
                    : "text-foreground/80 hover:bg-accent"
                }`
              }
            >
              <Icon className="size-4" />
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-border">
          <div className="text-xs text-muted-foreground mb-2 px-1">
            Angemeldet als
          </div>
          <div className="text-sm px-1 truncate" data-testid="operator-current-user">
            {user.email}
          </div>
          <button
            onClick={handleLogout}
            data-testid="operator-logout"
            className="mt-3 w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-foreground/80 hover:bg-accent"
          >
            <LogOut className="size-4" /> Abmelden
          </button>
        </div>
      </aside>
      <main className="flex-1 min-w-0">
        <div className="max-w-6xl mx-auto px-8 py-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
